import React, { useState, useEffect } from 'react';
import { animationIcons, iconsPath } from '../../utils/imagePath';
import AnimationDropdown from './AnimationDropdown';
import { ANIMATION_PROPERTY_CONFIGS } from './AnimationComponent/constants/animationPropertyConfig';
import { DividerRight } from '../../utils/gridUtils';
import {
  getAnimatableObject,
  objectHasGlbAnimation,
  resolveAnimationSceneObject,
} from './AnimationComponent/handlers/animationEffectHandlers';
import { TIME_PRECISION } from './utils/timelineUtils';

const AnimationRightPanel = ({
  // Animation effects and settings state
  animationEffectsExpanded,
  animationSettingsExpanded,
  // Animation effects data
  animationEffects,
  draggedEffect,
  // Animation settings
  animationSettings,
  selectedEffects,
  timelineEffects,
  completeImageObject,
  selectedObject,

  // Event handlers
  handleEffectDragStart,
  handleEffectDragEnd,
  toggleAnimationEffects,
  toggleAnimationSettings,
  toggleAxis,
  updateSetting,
  updateSelectedEffectsProperty,
  updatePosition,
  roundToPrecision,
  parseFloatWithPrecision,
  isMinimized,
  setIsMinimized,
}) => {
  const easingOptions = ['None', 'Ease-in', 'Ease-out', 'In-out'];
  const [errors, setErrors] = useState({});
  // Track in-progress typing for position/rotation/scale so we can enter negative values (e.g. "-5")
  const [editingPosition, setEditingPosition] = useState(null);
  const [editingNumber, setEditingNumber] = useState(null);
  // Same for default Start/End position inputs when no effect is selected
  const [editingDefaultPosition, setEditingDefaultPosition] = useState(null);

  // Clear editing state when selected effect changes
  useEffect(() => {
    setEditingPosition(null);
    setEditingNumber(null);
    setEditingDefaultPosition(null);
  }, [selectedEffects]);

  // Get the selected effect for dynamic properties
  const selectedEffect =
    selectedEffects.length === 1
      ? timelineEffects.find((effect) => effect.id === selectedEffects[0])
      : null;

  // Play Model only for GLBs that actually have animation clips.
  const visibleAnimationEffects = (() => {
    const scene = completeImageObject?.scene;
    const objectId =
      selectedObject ||
      selectedEffect?.objectId ||
      completeImageObject?.selectedObject?.name ||
      completeImageObject?.selectedObject?.id ||
      (typeof completeImageObject?.selectedObject === 'string'
        ? completeImageObject.selectedObject
        : null);
    const sceneObj = resolveAnimationSceneObject(scene, objectId);
    const hasClips = objectHasGlbAnimation(sceneObj);
    if (hasClips) return animationEffects || [];
    return (animationEffects || []).filter((effect) => effect.id !== 'playModel');
  })();

  // Seed clipName from the model's clips when Play Model has only an index
  useEffect(() => {
    if (!selectedEffect || selectedEffect.effectType !== 'playModel') return;
    if (
      typeof selectedEffect.clipName === 'string' &&
      selectedEffect.clipName.trim()
    ) {
      return;
    }

    const clipOptions = (() => {
      const scene = completeImageObject?.scene;
      if (!scene || !selectedEffect.objectId) return [];
      const sceneObj = resolveAnimationSceneObject(
        scene,
        selectedEffect.objectId
      );
      const animatable = getAnimatableObject(sceneObj);
      const clips = animatable?.userData?.animationClips || [];
      return clips.map((clip, index) => {
        const name =
          typeof clip?.name === 'string' && clip.name.trim()
            ? clip.name
            : `Clip ${index}`;
        return { value: name, index };
      });
    })();

    if (!clipOptions.length) return;

    const index =
      Number.isInteger(selectedEffect.clipIndex) && selectedEffect.clipIndex >= 0
        ? selectedEffect.clipIndex
        : 0;
    const matched = clipOptions[index] || clipOptions[0];
    if (!matched) return;

    if (typeof updateSelectedEffectsProperty === 'function') {
      updateSelectedEffectsProperty('clipName', matched.value);
      updateSelectedEffectsProperty('clipIndex', matched.index);
    }
  }, [
    selectedEffect?.id,
    selectedEffect?.effectType,
    selectedEffect?.clipName,
    selectedEffect?.clipIndex,
    selectedEffect?.objectId,
    completeImageObject,
    updateSelectedEffectsProperty,
  ]);

  // Get animation configuration for the selected effect
  const getAnimationConfig = (effectType) => {
    return (
      ANIMATION_PROPERTY_CONFIGS[effectType] || ANIMATION_PROPERTY_CONFIGS.move
    );
  };

  // Update animation property for selected effect
  const updateAnimationProperty = (key, value) => {
    if (!selectedEffect) return;

    if (typeof updateSelectedEffectsProperty === 'function') {
      updateSelectedEffectsProperty(key, value);
    } else {
      updateSetting(key, value);
    }
  };

  // Helper function to get property value from selected effect or default
  const getPropertyValue = (key, defaultValue) => {
    if (selectedEffect && selectedEffect[key] !== undefined) {
      return selectedEffect[key];
    }
    return animationSettings[key] !== undefined
      ? animationSettings[key]
      : defaultValue;
  };

  // Render property control based on type
  const renderPropertyControl = (control) => {
    const {
      type,
      key,
      label,
      min = 0,
      max = 100,
      step = 1,
      options = [],
      condition,
    } = control;
    const animationConfig = selectedEffect
      ? getAnimationConfig(selectedEffect.effectType)
      : null;
    const defaultValue = animationConfig
      ? animationConfig.properties[key]
      : null;
    const value = getPropertyValue(key, defaultValue);

    // Check condition for conditional controls
    if (condition) {
      const currentMovementType = getPropertyValue('movementType', 'linear');
      // Parse condition safely
      const expectedType = condition.match(/movementType === "([^"]+)"/)?.[1];
      if (expectedType && currentMovementType !== expectedType) {
        return null;
      }
    }

    const handleInputChange = (key, newValue) => {
      // Remove all previous errors except current field
      setErrors((prev) => {
        const updated = {};
        Object.keys(prev).forEach((k) => {
          updated[k] = k === key ? prev[k] : '';
        });
        return updated;
      });

      // Validate max
      if (newValue > max) {
        setErrors((prev) => ({
          ...prev,
          [key]: `Maximum allowed value is ${max}`,
        }));
        return; // don't update state if invalid
      }

      // Valid → update animation
      updateAnimationProperty(key, newValue);

      // Clear this field's error
      setErrors((prev) => ({
        ...prev,
        [key]: '',
      }));
    };

    switch (type) {
      // case 'number':
      //   return (
      //     <div key={key} className="flex flex-col">
      //       <label className="text-[10px] text-gray-400 mb-1">{label}</label>

      //       <div className="flex gap-28 items-center bg-[#2B2C2F] rounded px-2 py-1">
      //         <input
      //           type="number"
      //           value={value || 0}
      //           onChange={(e) =>
      //             handleInputChange(key, parseFloat(e.target.value))
      //           }
      //           className="w-12 bg-transparent text-[10px] text-white focus:outline-none"
      //           step={step}
      //           min={min}
      //           max={max}
      //         />

      //         <div className="flex flex-col ml-2">
      //           <button
      //             onClick={() => handleInputChange(key, (value || 0) + step)}
      //             className="text-gray-400 hover:text-white text-[10px] leading-none"
      //           >
      //             ▲
      //           </button>
      //           <button
      //             onClick={() =>
      //               handleInputChange(key, Math.max(min, (value || 0) - step))
      //             }
      //             className="text-gray-400 hover:text-white text-[10px] leading-none"
      //           >
      //             ▼
      //           </button>
      //         </div>
      //       </div>

      //       {/* ❗ show error only for this field */}
      //       {errors[key] && (
      //         <p className="text-[#EE2B2A] text-[10px] mt-1">{errors[key]}</p>
      //       )}
      //     </div>
      //   );

      case 'number': {
        // Special UI: for `playModel.clipIndex`, show the GLB animation clip names
        // instead of a raw numeric index.
        if (
          selectedEffect?.effectType === 'playModel' &&
          key === 'clipIndex'
        ) {
          const scene = completeImageObject?.scene;
          const objectId = selectedEffect?.objectId;
          const sceneObj = scene && objectId
            ? resolveAnimationSceneObject(scene, objectId)
            : null;
          const animatable = sceneObj ? getAnimatableObject(sceneObj) : null;
          const clips = animatable?.userData?.animationClips || [];

          const clipOptions = clips.map((clip, index) => {
            const name =
              typeof clip?.name === 'string' && clip.name.trim()
                ? clip.name
                : `Clip ${index}`;
            return { value: String(index), label: name };
          });

          if (!clipOptions.length) {
            // Fallback to numeric input if the model has no clips.
          } else {
            const numericIndex =
              Number.isInteger(value) && value >= 0 ? value : 0;
            const activeClip = clips[numericIndex];
            const activeName =
              typeof activeClip?.name === 'string' && activeClip.name.trim()
                ? activeClip.name
                : `Clip ${numericIndex}`;

            return (
              <div key={key} className="flex flex-col">
                <AnimationDropdown
                  label={label}
                  value={String(numericIndex)}
                  onChange={(e) => {
                    const nextIndex = Number(e?.target?.value);
                    const nextClip = clips[nextIndex];
                    const nextName =
                      typeof nextClip?.name === 'string' && nextClip.name.trim()
                        ? nextClip.name
                        : `Clip ${nextIndex}`;
                    updateSelectedEffectsProperty('clipIndex', nextIndex);
                    updateSelectedEffectsProperty('clipName', nextName);
                  }}
                  options={clipOptions}
                  placeholder={`Select ${label.toLowerCase()}`}
                />
              </div>
            );
          }
        }

        const error = errors[key];
        const isEditing = editingNumber?.key === key;
        const precision =
          control.precision ??
          (key === 'animationSpeed' || key === 'playSpeed'
            ? TIME_PRECISION
            : String(step).includes('.')
              ? String(step).split('.')[1].length
              : 0);
        const formatValue = (num) =>
          String(roundToPrecision(num ?? min, precision));
        const displayValue = isEditing
          ? editingNumber.rawValue
          : formatValue(value ?? min);

        const commitNumberValue = (raw) => {
          if (raw === '' || raw === '-' || raw === '.') {
            updateAnimationProperty(key, min);
            return;
          }

          const num = Number(raw);
          const rounded = roundToPrecision(num, precision);

          if (Number.isNaN(num)) {
            updateAnimationProperty(key, min);
            return;
          }

          if (rounded < min) {
            updateAnimationProperty(key, min);
            return;
          }

          if (max !== undefined && rounded > max) {
            updateAnimationProperty(key, max);
            return;
          }

          updateAnimationProperty(key, rounded);
        };

        const isIncompleteNumber = (raw) =>
          raw === '' || raw === '-' || raw === '.' || raw.endsWith('.');

        return (
          <div key={key} className="flex flex-col">
            <label className="text-[10px] text-gray-400 mb-1">{label}</label>

            <div className="flex gap-28 items-center bg-[#2B2C2F] rounded px-2 py-1">
              <input
                type="text"
                inputMode="decimal"
                value={displayValue}
                className="w-12 bg-transparent text-[10px] text-white focus:outline-none"
                onFocus={() =>
                  setEditingNumber({
                    key,
                    rawValue: formatValue(value ?? min),
                  })
                }
                onChange={(e) => {
                  const raw = e.target.value;

                  if (raw !== '' && !/^-?\d*\.?\d*$/.test(raw)) {
                    return;
                  }

                  setEditingNumber({ key, rawValue: raw });

                  if (isIncompleteNumber(raw)) {
                    setErrors((prev) => ({ ...prev, [key]: '' }));
                    return;
                  }

                  const num = Number(raw);
                  const rounded = roundToPrecision(num, precision);

                  if (Number.isNaN(num)) {
                    setErrors((prev) => ({
                      ...prev,
                      [key]: 'Invalid number',
                    }));
                    return;
                  }

                  if (max !== undefined && rounded > max) {
                    setErrors((prev) => ({
                      ...prev,
                      [key]: `Maximum value is ${max}`,
                    }));
                    return;
                  }

                  setErrors((prev) => {
                    const copy = { ...prev };
                    delete copy[key];
                    return copy;
                  });

                  if (rounded >= min) {
                    updateAnimationProperty(key, rounded);
                  }
                }}
                onBlur={(e) => {
                  commitNumberValue(e.target.value);
                  setEditingNumber(null);
                  setErrors((prev) => ({ ...prev, [key]: '' }));
                }}
                onKeyDown={(e) => {
                  if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
                  e.preventDefault();
                  const raw =
                    editingNumber?.key === key
                      ? editingNumber.rawValue
                      : formatValue(value ?? min);
                  let current = Number(raw);
                  if (Number.isNaN(current)) {
                    current = min;
                  }

                  const delta = e.key === 'ArrowUp' ? step : -step;
                  const nextValue = roundToPrecision(
                    Math.max(
                      min,
                      max !== undefined
                        ? Math.min(max, current + delta)
                        : current + delta
                    ),
                    precision
                  );

                  setEditingNumber(null);
                  handleInputChange(key, nextValue);
                }}
              />

              <div className="flex flex-col ml-2">
                <button
                  type="button"
                  className="text-gray-400 hover:text-white text-[10px] leading-none"
                  onClick={() => {
                    setEditingNumber(null);
                    const base = Number(value);
                    const next = isNaN(base)
                      ? min
                      : roundToPrecision(base + step, precision);
                    handleInputChange(key, next);
                  }}
                >
                  ▲
                </button>

                <button
                  type="button"
                  className="text-gray-400 hover:text-white text-[10px] leading-none"
                  onClick={() => {
                    setEditingNumber(null);
                    const base = Number(value);
                    const next = isNaN(base)
                      ? min
                      : roundToPrecision(Math.max(min, base - step), precision);
                    handleInputChange(key, next);
                  }}
                >
                  ▼
                </button>
              </div>
            </div>

            {/* ❗ error message (no jump) */}
            {error && (
              <p className="text-[#EE2B2A] text-[10px] mt-1">{error}</p>
            )}
          </div>
        );
      }

      case 'slider':
        return (
          <div key={key} className="flex flex-col">
            <label className="text-[10px] text-gray-400 mb-1">
              {label}: {(value || 0).toFixed(2)}
            </label>
            <input
              type="range"
              min={min}
              max={max}
              step={step}
              value={value || 0}
              onChange={(e) =>
                updateAnimationProperty(key, parseFloat(e.target.value))
              }
              className="w-full h-1 bg-[#2B2C2F] rounded-lg appearance-none cursor-pointer slider"
              style={{
                background: `linear-gradient(to right, #dc2626 0%, #dc2626 ${(((value || 0) - min) / (max - min)) * 100}%, #2B2C2F ${(((value || 0) - min) / (max - min)) * 100}%, #2B2C2F 100%)`,
              }}
            />
          </div>
        );

      case 'select':
        return (
          <div key={key} className="flex flex-col">
            <AnimationDropdown
              label={label}
              value={value || options[0]}
              onChange={(newValue) =>
                updateAnimationProperty(key, newValue.target.value)
              }
              options={options.map((option) => ({
                value: option,
                label: option,
              }))}
              placeholder={`Select ${label.toLowerCase()}`}
            />
          </div>
        );

      case 'checkbox':
        return (
          <div
            key={key}
            className="flex items-center gap-2 cursor-pointer mb-2"
            onClick={() => updateAnimationProperty(key, !value)}
          >
            <div className="w-4 h-4 bg-[#2B2C2F] border border-gray-700 flex items-center justify-center rounded">
              {value && (
                <svg
                  className="h-3 w-3 text-white"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </div>
            <span className="text-[10px] text-white">{label}</span>
          </div>
        );

      case 'axes': {
        const normalizeAxes = (val) => {
          if (typeof val === 'string' && ['x', 'y', 'z'].includes(val)) {
            return { x: val === 'x', y: val === 'y', z: val === 'z' };
          }
          if (val && typeof val === 'object') return val;
          return null;
        };

        const normalized = normalizeAxes(value);
        const normalizedDefault = normalizeAxes(defaultValue);
        const resolvedValue = normalized
          ? normalized
          : normalizedDefault && Object.values(normalizedDefault).some(Boolean)
            ? normalizedDefault
            : { x: true, y: false, z: false };

        return (
          <div key={key} className="flex flex-col">
            <label className="text-[10px] text-gray-400 mb-2">{label}</label>
            <div className="flex space-x-3">
              {['x', 'y', 'z'].map((axis) => (
                <div
                  key={axis}
                  className="flex items-center gap-1 cursor-pointer"
                  onClick={() => {
                    const isBounce = selectedEffect?.effectType === 'bounce';
                    let newValue;

                    if (isBounce) {
                      if (resolvedValue[axis]) return;
                      newValue = {
                        x: axis === 'x',
                        y: axis === 'y',
                        z: axis === 'z',
                      };
                    } else {
                      newValue = {
                        ...resolvedValue,
                        [axis]: !resolvedValue[axis],
                      };
                    }

                    updateAnimationProperty(key, newValue);
                  }}
                >
                  <div className="w-3 h-3 bg-[#2B2C2F] border border-gray-600 rounded flex items-center justify-center">
                    {resolvedValue[axis] && (
                      <svg
                        className="h-2 w-2 text-white"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                  <span className="text-[10px] text-gray-300 uppercase">
                    {axis}
                  </span>
                </div>
              ))}
            </div>
          </div>
        );
      }
      case 'position':
      case 'rotation':
      case 'scale': {
        // Only apply min/max when explicitly set on control (e.g. rotation degrees).
        // Position/scale without min/max allow negative values (e.g. move start/end).
        const hasMin = control.min !== undefined;
        const hasMax = control.max !== undefined;
        const isEditing = (a) =>
          editingPosition?.key === key && editingPosition?.axis === a;
        return (
          <div key={key} className="flex flex-col">
            <label className="text-[10px] text-gray-400 mb-1">{label}</label>
            <div className="flex space-x-2">
              {['x', 'y', 'z'].map((axis) => {
                const currentNum = (value || {})[axis];
                const displayValue = isEditing(axis)
                  ? editingPosition.rawValue
                  : currentNum !== undefined && currentNum !== null
                    ? currentNum
                    : '';
                return (
                  <div key={axis} className="flex items-center space-x-1">
                    <span className="text-[10px] text-gray-400 uppercase">
                      {axis}
                    </span>
                    <input
                      type="number"
                      value={displayValue}
                      onFocus={() =>
                        setEditingPosition({
                          key,
                          axis,
                          rawValue: String(currentNum ?? ''),
                        })
                      }
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === '' || raw === '-') {
                          setEditingPosition((prev) =>
                            prev?.key === key && prev?.axis === axis
                              ? { ...prev, rawValue: raw }
                              : { key, axis, rawValue: raw }
                          );
                          return;
                        }
                        const num = parseFloat(raw);
                        if (!Number.isNaN(num)) {
                          updateAnimationProperty(key, {
                            ...(value || {}),
                            [axis]: num,
                          });
                          setEditingPosition((prev) =>
                            prev?.key === key && prev?.axis === axis
                              ? null
                              : prev
                          );
                        } else {
                          setEditingPosition((prev) =>
                            prev?.key === key && prev?.axis === axis
                              ? { ...prev, rawValue: raw }
                              : { key, axis, rawValue: raw }
                          );
                        }
                      }}
                      onBlur={() => {
                        if (
                          editingPosition?.key !== key ||
                          editingPosition?.axis !== axis
                        ) {
                          return;
                        }
                        const raw = editingPosition.rawValue;
                        const num =
                          raw === '' || raw === '-' ? 0 : parseFloat(raw);
                        updateAnimationProperty(key, {
                          ...(value || {}),
                          [axis]: Number.isNaN(num) ? 0 : num,
                        });
                        setEditingPosition((prev) =>
                          prev?.key === key && prev?.axis === axis ? null : prev
                        );
                      }}
                      onKeyDown={(e) => {
                        if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')
                          return;
                        e.preventDefault();
                        const raw =
                          editingPosition?.key === key &&
                          editingPosition?.axis === axis
                            ? editingPosition.rawValue
                            : String(currentNum ?? 0);
                        let current = Number(raw);
                        if (Number.isNaN(current)) {
                          current = 0;
                        }

                        const delta = e.key === 'ArrowUp' ? step : -step;
                        let nextValue = current + delta;
                        if (hasMin) {
                          nextValue = Math.max(min, nextValue);
                        }
                        if (hasMax) {
                          nextValue = Math.min(max, nextValue);
                        }

                        updateAnimationProperty(key, {
                          ...(value || {}),
                          [axis]: nextValue,
                        });
                        setEditingPosition(null);
                      }}
                      className="w-10 bg-[#2B2C2F] border border-gray-600 text-[10px] text-white px-1 py-0.5 rounded focus:outline-none"
                      step={step}
                      {...(hasMin && { min: control.min })}
                      {...(hasMax && { max: control.max })}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className="w-60 bg-[#131418] border-l border-[#4D4D4D] flex flex-col">
      <div
        className="flex-1 overflow-y-auto"
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: '#444447 #1a1b1e',
        }}
      >
        {/* Animation Effects Section */}
        <div className="py-[0.125rem] px-3 border-b border-white/20">
          <div
            className="flex items-center justify-between mb-1 cursor-pointer p-1 transition-colors duration-200 rounded"
            onClick={toggleAnimationEffects}
          >
            <h3 className="text-xs text-[#EE2B2E] font-medium mt-[7px]">
              Animation Effects
            </h3>
            {!isMinimized && (
              <img
                className={`w-3 h-3 text-gray-400 transition-transform duration-200 mt-[7px] ${animationEffectsExpanded ? 'rotate-0' : 'rotate-180'}`}
                src={iconsPath?.vector6Image}
                alt=""
              />
            )}
          </div>
          <div
            className={`transition-all duration-300 ease-in-out ${animationEffectsExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}
          >
            <div className="grid grid-cols-2 px-1 py-2 gap-3">
              {visibleAnimationEffects.map((effect) => (
                <div
                  key={effect.id}
                  className={`bg-[#2B2C2F] hover:bg-[#EE2B2A] my-box  rounded-[10px] p-[6px] flex flex-col items-center space-y-3 cursor-pointer transition-all duration-200 transform hover:scale-105 ${draggedEffect?.id === effect.id ? 'opacity-50 scale-95' : ''}`}
                  draggable
                  onDragStart={(e) => handleEffectDragStart(e, effect)}
                  onDragEnd={handleEffectDragEnd}
                >
                  <div className="flex flex-col items-center gap-2">
                    <img
                      className="w-[18px] h-[18px] text-[#C8BEBE] pointer-events-none"
                      src={effect.icon}
                      alt=""
                    />
                    <span className="text-[10px] text-[#C8BEBE] font-medium pointer-events-none text-center">
                      {effect.name}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Animation Settings Section */}
        {selectedEffects.length > 0 && (
          <div className="py-[0.125rem] px-3">
            <div
              className="flex items-center justify-between mb-1 cursor-pointer p-1 transition-colors duration-200 rounded"
              onClick={toggleAnimationSettings}
            >
              <h3 className="text-xs text-[#EE2B2A] font-medium mt-[7px]">
                Animation Settings
              </h3>
              <img
                className={`w-3 h-3 text-gray-400 transition-transform duration-200 mt-[7px] ${animationSettingsExpanded ? 'rotate-0' : 'rotate-180'}`}
                src={iconsPath?.vector6Image}
                alt=""
              />
            </div>
            <div
              className={`transition-all duration-300 ease-in-out ${animationSettingsExpanded ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}
            >
              <div className="space-y-4 p-1">
                {/* Common Settings - Duration and Start Time */}
                <div className="flex justify-between items-center gap-5 border-b border-white/20 pb-4">
                  <div className="flex flex-col flex-1">
                    <label className="text-[10px] text-[#A6A6A6] mb-1">
                      Duration
                    </label>
                    <div className="flex items-center bg-[#2B2C2F] rounded px-2 py-1">
                      <input
                        type="number"
                        value={roundToPrecision(
                          animationSettings.duration,
                          TIME_PRECISION
                        )}
                        onChange={(e) =>
                          updateSetting(
                            'duration',
                            Math.min(
                              120,
                              Math.max(
                                0.001,
                                parseFloatWithPrecision(
                                  e.target.value,
                                  TIME_PRECISION
                                )
                              )
                            )
                          )
                        }
                        onKeyDown={(e) => {
                          if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')
                            return;
                          e.preventDefault();
                          const current =
                            parseFloatWithPrecision(
                              String(animationSettings.duration ?? 0),
                              TIME_PRECISION
                            ) || 0;
                          const delta = e.key === 'ArrowUp' ? 0.1 : -0.1;
                          const next = Math.min(
                            120,
                            Math.max(
                              0.001,
                              roundToPrecision(current + delta, TIME_PRECISION)
                            )
                          );
                          updateSetting('duration', next);
                        }}
                        className="w-16 bg-transparent text-[10px] text-white focus:outline-none"
                        step="0.001"
                        min="0.001"
                        max="120"
                      />
                      <span className="text-[10px] text-gray-400 ml-1">s</span>

                      <div className="flex flex-col ml-2">
                        <button
                          onClick={() =>
                            updateSetting(
                              'duration',
                              Math.min(
                                120,
                                roundToPrecision(
                                  animationSettings.duration + 0.1,
                                  TIME_PRECISION
                                )
                              )
                            )
                          }
                          className="text-gray-400 hover:text-white text-[10px] leading-none"
                        >
                          ▲
                        </button>

                        <button
                          onClick={() =>
                            updateSetting(
                              'duration',
                              Math.max(
                                0.001,
                                roundToPrecision(
                                  animationSettings.duration - 0.1,
                                  TIME_PRECISION
                                )
                              )
                            )
                          }
                          className="text-gray-400 hover:text-white text-[10px] leading-none"
                        >
                          ▼
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* <div className="flex flex-col flex-1">
                  <label className="text-[10px] text-[#A6A6A6] mb-1">
                    Duration
                  </label>
                  <div className="flex items-center bg-[#2B2C2F] rounded px-2 py-1">
                    <input
                      type="number"
                      value={roundToPrecision(animationSettings.duration, 1)}
                      onChange={(e) =>
                        updateSetting(
                          'duration',
                          Math.max(
                            0.1,
                            parseFloatWithPrecision(e.target.value, 1)
                          )
                        )
                      }
                      className="w-12 bg-transparent text-[10px] text-white focus:outline-none"
                      step="0.1"
                      min="0.1"
                    />
                    <span className="text-[10px] text-gray-400 ml-1">s</span>
                    <div className="flex flex-col ml-2">
                      <button
                        onClick={() =>
                          updateSetting(
                            'duration',
                            roundToPrecision(
                              animationSettings.duration + 0.1,
                              1
                            )
                          )
                        }
                        className="text-gray-400 hover:text-white text-[10px] leading-none"
                      >
                        ▲
                      </button>
                      <button
                        onClick={() =>
                          updateSetting(
                            'duration',
                            Math.max(
                              0.1,
                              roundToPrecision(
                                animationSettings.duration - 0.1,
                                1
                              )
                            )
                          )
                        }
                        className="text-gray-400 hover:text-white text-[10px] leading-none"
                      >
                        ▼
                      </button>
                    </div>
                  </div>
                </div> */}

                  {/* <div className="flex flex-col flex-1">
                  <label className="text-[10px] text-[#A6A6A6] mb-1">
                    Start Time
                  </label>
                  <div className="flex items-center bg-[#2B2C2F] rounded px-2 py-1">
                    <input
                      type="number"
                      value={roundToPrecision(animationSettings.startTime, 1)}
                      onChange={(e) => {
                        const newStartTime = Math.max(
                          0,
                          parseFloatWithPrecision(e.target.value, 1)
                        );
                        updateSetting('startTime', newStartTime);
                      }}
                      className="w-12 bg-transparent text-[10px] text-white focus:outline-none"
                      step="0.1"
                      min="0"
                    />
                    <span className="text-[10px] text-gray-400 ml-1">s</span>
                    <div className="flex flex-col ml-2">
                      <button
                        onClick={() => {
                          const newStartTime = roundToPrecision(
                            animationSettings.startTime + 0.1,
                            1
                          );
                          updateSetting('startTime', newStartTime);
                        }}
                        className="text-gray-400 hover:text-white text-[10px] leading-none"
                      >
                        ▲
                      </button>
                      <button
                        onClick={() => {
                          const newStartTime = Math.max(
                            0,
                            roundToPrecision(
                              animationSettings.startTime - 0.1,
                              1
                            )
                          );
                          updateSetting('startTime', newStartTime);
                        }}
                        className="text-gray-400 hover:text-white text-[10px] leading-none"
                      >
                        ▼
                      </button>
                    </div>
                  </div>
                </div> */}
                  <div className="flex flex-col flex-1">
                    <label className="text-[10px] text-[#A6A6A6] mb-1">
                      Start Time
                    </label>

                    <div className="flex items-center bg-[#2B2C2F] rounded px-2 py-1">
                      <input
                        type="number"
                        value={roundToPrecision(
                          animationSettings.startTime,
                          TIME_PRECISION
                        )}
                        onChange={(e) => {
                          const newStartTime = Math.min(
                            120,
                            Math.max(
                              0,
                              parseFloatWithPrecision(
                                e.target.value,
                                TIME_PRECISION
                              )
                            )
                          );
                          updateSetting('startTime', newStartTime);
                        }}
                        onKeyDown={(e) => {
                          if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')
                            return;
                          e.preventDefault();
                          const current =
                            parseFloatWithPrecision(
                              String(animationSettings.startTime ?? 0),
                              TIME_PRECISION
                            ) || 0;
                          const delta = e.key === 'ArrowUp' ? 0.1 : -0.1;
                          const next = Math.min(
                            120,
                            Math.max(
                              0,
                              roundToPrecision(current + delta, TIME_PRECISION)
                            )
                          );
                          updateSetting('startTime', next);
                        }}
                        className="w-16 bg-transparent text-[10px] text-white focus:outline-none"
                        step="0.001"
                        min="0"
                        max="120"
                      />

                      <span className="text-[10px] text-gray-400 ml-1">s</span>

                      <div className="flex flex-col ml-2">
                        <button
                          onClick={() => {
                            const newStartTime = Math.min(
                              120,
                              roundToPrecision(
                                animationSettings.startTime + 0.1,
                                TIME_PRECISION
                              )
                            );
                            updateSetting('startTime', newStartTime);
                          }}
                          className="text-gray-400 hover:text-white text-[10px] leading-none"
                        >
                          ▲
                        </button>

                        <button
                          onClick={() => {
                            const newStartTime = Math.max(
                              0,
                              roundToPrecision(
                                animationSettings.startTime - 0.1,
                                TIME_PRECISION
                              )
                            );
                            updateSetting('startTime', newStartTime);
                          }}
                          className="text-gray-400 hover:text-white text-[10px] leading-none"
                        >
                          ▼
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Easing */}
                <div>
                  <label className="text-xs text-[#ffffff] mb-2 block">
                    Easing
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {easingOptions.map((option, index) => (
                      <div
                        key={option}
                        className={
                          index === 3 ? 'col-span-3 flex justify-start' : ''
                        }
                      >
                        <button
                          onClick={() => updateSetting('easing', option)}
                          className={`w-[60px] h-[31px] text-[10px] transition-all duration-200 rounded-[50px] flex items-center justify-center transform hover:scale-105 ${
                            animationSettings.easing === option
                              ? 'bg-[#EE2B2A] text-white shadow-lg'
                              : 'bg-[#2B2C2F] text-gray-300 hover:bg-[#EE2B2A] hover:text-white'
                          }`}
                        >
                          {option}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Dynamic Animation-Specific Properties */}
                {selectedEffect ? (
                  <div>
                    <div className="border-t border-white/20 pt-4 mb-4">
                      <h4 className="text-xs text-[#EE2B2A] font-medium mb-3">
                        {getAnimationConfig(selectedEffect.effectType).name}{' '}
                        Properties
                      </h4>
                      <div className="space-y-3">
                        {getAnimationConfig(
                          selectedEffect.effectType
                        ).controls.map(renderPropertyControl)}
                      </div>
                    </div>
                  </div>
                ) : // Default fallback when no specific effect is selected
                // <div className="flex flex-col gap-4">
                //   <div className="border-t border-white/20 pt-4">
                //     <label className="text-xs text-gray-400 mb-2 block">
                //       Active Axes
                //     </label>
                //     <div className="flex space-x-3">
                //       {['x', 'y', 'z'].map((axis) => (
                //         <div
                //           key={axis}
                //           className="flex items-center gap-1 cursor-pointer"
                //           onClick={() => toggleAxis(axis)}
                //         >
                //           <div className="w-3 h-3  border border-gray-600 rounded flex items-center justify-center">
                //             {animationSettings.activeAxes[axis] && (
                //               <svg
                //                 className="h-2 w-2 text-white"
                //                 viewBox="0 0 20 20"
                //                 fill="currentColor"
                //               >
                //                 <path
                //                   fillRule="evenodd"
                //                   d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                //                   clipRule="evenodd"
                //                 />
                //               </svg>
                //             )}
                //           </div>
                //           <span className="text-[10px] text-gray-300 uppercase">
                //             {axis}
                //           </span>
                //         </div>
                //       ))}
                //     </div>
                //   </div>

                //   {['startPosition', 'endPosition'].map((positionType) => (
                //     <div key={positionType}>
                //       <label className="text-xs text-[#A6A6A6] mb-2 block">
                //         {positionType === 'startPosition'
                //           ? 'Start Position'
                //           : 'End Position'}
                //       </label>
                //       <div className="flex space-x-2">
                //         {['x', 'y', 'z'].map((axis) => (
                //           <div
                //             key={axis}
                //             className="flex items-center space-x-1"
                //           >
                //             <span className="text-[10px] text-gray-400 uppercase">
                //               {axis}
                //             </span>
                //             <input
                //               type="number"
                //               value={animationSettings[positionType][axis]}
                //               onChange={(e) =>
                //                 updatePosition(
                //                   positionType,
                //                   axis,
                //                   e.target.value
                //                 )
                //               }
                //               className="w-10 bg-[#2B2C2F] border-[0.5px] border-[#2C2C2C] text-[10px] text-white px-1 py-0.5 rounded focus:outline-none"
                //               step="0.1"
                //             />
                //           </div>
                //         ))}
                //       </div>
                //     </div>
                //   ))}
                //   <DividerRight />
                // </div>
                null}

                {/* Loop Option */}
                <div
                  className="flex items-center gap-2 cursor-pointer transition-colors duration-200"
                  onClick={() => updateSetting('loop', !selectedEffect?.loop)}
                >
                  <div className="w-4 h-4 bg-[#2B2C2F] border border-[#393939] flex items-center justify-center rounded transition-colors duration-200">
                    {selectedEffect?.loop && (
                      <svg
                        className="h-3 w-3 text-white"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                  <span className="text-[10px] text-white">Loop Animation</span>
                </div>

                {/* Selected Effects Info */}
                {selectedEffects.length > 0 && (
                  <div className="mt-4 p-3 bg-[#2B2C2F] rounded-lg border border-gray-600">
                    <div className="text-[10px] text-gray-400 mb-1">
                      Selected: {selectedEffects.length} effect
                      {selectedEffects.length > 1 ? 's' : ''}
                    </div>
                    {selectedEffects.length === 1 && (
                      <div className="space-y-1">
                        <div className="text-[10px] text-white font-medium">
                          {timelineEffects.find(
                            (e) => e.id === selectedEffects[0]
                          )?.name || 'Unknown'}
                        </div>
                        <div className="text-[9px] text-gray-500">
                          Type: {selectedEffect?.effectType || 'unknown'}
                        </div>
                      </div>
                    )}
                    {selectedEffects.length > 1 && (
                      <div className="text-[9px] text-gray-500 mt-1">
                        Multiple effects selected - common properties shown
                      </div>
                    )}
                  </div>
                )}

                {/* Help Text */}
                {!selectedEffect && selectedEffects.length === 0 && (
                  <div className="mt-4 p-3 bg-[#1A1B1F] rounded-lg border border-gray-600">
                    <div className="text-[10px] text-gray-400 text-center">
                      Select an effect on the timeline to edit its specific
                      properties
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Custom Styles for slider */}
      <style>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: #dc2626;
          cursor: pointer;
          border: 2px solid #ffffff;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
        }

        .slider::-moz-range-thumb {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: #dc2626;
          cursor: pointer;
          border: 2px solid #ffffff;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
        }
      `}</style>
    </div>
  );
};

export default AnimationRightPanel;
